import React from 'react';
import { di } from 'react-magnetic-di';
import { fg } from '@atlaskit/platform-feature-flags';
import Spinner from '@atlaskit/spinner';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';
import { DisplayMode, FormQuestionType, RenderForm } from '@atlassian/proforma-common-core/form-system';
import { FormViewMode, useFormIndexState, useSelectedFormStoreState } from '../../../../controllers/form-index';
import { useHandleSaveForm } from '../../../../controllers/form-index/use-handle-save-form';
import { useHandleSubmitForm } from '../../../../controllers/form-index/use-handle-submit-form';
import { useWithDiscardChangeNotice } from '../../../../controllers/form-index/use-with-discard-change-notice';
import { getSimpleFormAnalytics } from '../../../../utils/simple-form-analytics';
import { EditButtons } from './edit-buttons';
import { ErrorMessage } from './error';
import { PermissionErrorMessage } from './permissionError';
import { RightAlign } from './styled';
// The main body of a form card, displayed when a form is expanded. It renders the selected form, and provides buttons
// for manipulating the form state (i.e. edit, submit, reopen).
export const FormCardBody = ({
  form,
  ufoExperience,
  linkedFieldNames,
  onCancelEdit,
  projectType
}) => {
  // Hooks for retrieving state relevant to the selected form
  const {
    formViewMode
  } = useFormIndexState();
  const {
    selectedFormStore: formStore,
    loading,
    error
  } = useSelectedFormStoreState();

  // Hooks providing functions for transitioning the form between states
  const {
    isSaving,
    handleSaveForm
  } = useHandleSaveForm();
  const {
    isSubmitting,
    handleSubmitForm
  } = useHandleSubmitForm(projectType);
  const withDiscardChangeNotice = useWithDiscardChangeNotice();

  // Fire a submitForm UI analytics event, then submit the form.
  const handleClickSubmit = async (_e, analyticsEvent) => {
    fireUIAnalytics(analyticsEvent, 'submitForm', {
      ...getSimpleFormAnalytics(formStore === null || formStore === void 0 ? void 0 : formStore.formId, formStore === null || formStore === void 0 ? void 0 : formStore.templateFormId, formStore === null || formStore === void 0 ? void 0 : formStore.status, formStore === null || formStore === void 0 ? void 0 : formStore.internal),
      formFields: {
        cc: formStore === null || formStore === void 0 ? void 0 : formStore.questions.some(question => question.formQuestion.type === FormQuestionType.ChoiceCascading)
      },
      hasCascadingFields: formStore === null || formStore === void 0 ? void 0 : formStore.questions.some(question => question.formQuestion.type === FormQuestionType.ChoiceCascading),
      translated: (formStore === null || formStore === void 0 ? void 0 : formStore.translatedLocale) && (formStore === null || formStore === void 0 ? void 0 : formStore.primaryLocale) !== (formStore === null || formStore === void 0 ? void 0 : formStore.translatedLocale)
    });
    await handleSubmitForm(projectType);
  };

  // Fire a saveForm UI analytics event, then save the form.
  const handleClickSave = async (_e, analyticsEvent) => {
    fireUIAnalytics(analyticsEvent, 'saveForm', getSimpleFormAnalytics(formStore === null || formStore === void 0 ? void 0 : formStore.formId, formStore === null || formStore === void 0 ? void 0 : formStore.templateFormId, formStore === null || formStore === void 0 ? void 0 : formStore.status, formStore === null || formStore === void 0 ? void 0 : formStore.internal));
    await handleSaveForm();
  };

  // Fire a cancelForm UI analytics event, then save the form.
  const handleClickCancel = async (_e, analyticsEvent) => {
    fireUIAnalytics(analyticsEvent, 'cancelEdit', getSimpleFormAnalytics(formStore === null || formStore === void 0 ? void 0 : formStore.formId, formStore === null || formStore === void 0 ? void 0 : formStore.templateFormId, formStore === null || formStore === void 0 ? void 0 : formStore.status, formStore === null || formStore === void 0 ? void 0 : formStore.internal));
    withDiscardChangeNotice(onCancelEdit);
  };

  // If the form data is still loading, show a spinner
  if (loading) {
    return /*#__PURE__*/React.createElement(Spinner, null);
  }
  if (!(form !== null && form !== void 0 && form.hasAccess) && fg('granular_permissions_on_forms_ga')) {
    return /*#__PURE__*/React.createElement(PermissionErrorMessage, null);
  }

  // If there was an error loading the form, or the formStore has no form loaded, display an error message
  if (error || !formStore || formStore.formId !== form.id) {
    return /*#__PURE__*/React.createElement(ErrorMessage, null);
  }
  const isEditingForm = formViewMode === FormViewMode.Edit;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(RenderForm, {
    formStore: formStore,
    displayMode: isEditingForm ? DisplayMode.Edit : DisplayMode.View,
    key: form.id,
    ufoExperience: ufoExperience,
    backgroundColor: "var(--ds-background-neutral-subtle, transparent)",
    linkedFieldNames: linkedFieldNames
  }), /*#__PURE__*/React.createElement(RightAlign, null, isEditingForm && /*#__PURE__*/React.createElement(EditButtons, {
    isSubmitting: isSubmitting,
    isSaving: isSaving,
    onSubmit: handleClickSubmit,
    onSave: handleClickSave,
    onCancel: handleClickCancel
  })));
};